import {
    AttributeMapping,
    AttributeReference,
    IDenormalizeEntity,
    IDenormalizeInfo,
    ILookupInfo,
    IMappingJoinPair,
    IModel,
    IUnpivotAttribute,
    IUnpivotInfo,
    MappingDirective,
    MappingType,
    Metamodel,
    Named
} from 'shell-models-lib';
import { MapperConnectionReference } from 'app/model/adfDataMapper/mapperConnectionReference';
import { IMapperLookupInfo, IMapperUnpivotAttribute, IMapperUnpivotInfo, MapperAttributeMapping } from 'app/model/adfDataMapper/mapperAttributeMapping';
import { MapperAttributeReference } from 'app/model/adfDataMapper/mapperAttributeReference';
import { AdfDataMapperValidations } from 'app/model/adfDataMapper/adfDataMapper.validations';
import { IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { SequentialTask } from 'app/model/adfDataMapper/taskExecutor/sequentialTask';
import { DslConnectorConstants } from '../adapters/mappingArtifact/DslConnectorConstants';

export interface IMapperTableDenormalizeInfo {
    entities: IMapperTableDenormalizeEntity[];
}

export interface IMapperTableDenormalizeEntity {
    joinPairs: IMapperJoinPair[];
    toEntity: string;
    toEntityConnectionReference: MapperConnectionReference;
}

export interface IMapperJoinPair {
    fromAttribute: MapperAttributeReference;
    toAttribute: string;
    operator?: string;
    expression?: string;
}

@Metamodel.model({
    name: 'DataMapperMapping',
    display: ['DataMapperMapping', 'DataMapperMappings'],
    reusable: true,
    referable: true,
    validator: AdfDataMapperValidations.validateDataMapping
})
export class DataMapperMapping extends Named {
    public static readonly MODEL: IModel;

    public targetTable: IMapperTable;
    public sourceTable: IMapperTable;
    public sourceConnectionReference: MapperConnectionReference;
    public targetConnectionReference: MapperConnectionReference;
    public sourceDenormalizeInfo?: IMapperTableDenormalizeInfo;
    public attributeMappings: MapperAttributeMapping[];
    public isSourceTargetSchemaLoading: boolean = false;
    public isSchemaForKeysLoading: boolean = false;
    public hasSchemaLoadingError?: boolean;
    public showSchemaLoadingError?: boolean;
    public showSchemaLoadingErrorForKeys?: boolean;
    public schemaLoadingErrorMessage?: string;
    public numUnMappedColumns: number = 0;
    public isAttributeAutoMappingDone: boolean = false;
    public showSchemaUpdateWarning: boolean = false;
    public showSchemaUpdateOptions: boolean = false;
    public readonly schemaImportTask = new SequentialTask();

    constructor(name: string = '') {
        super(name);
        this.name = name;
        this.attributeMappings = [];
        this.sourceDenormalizeInfo = {
            entities: []
        };
    }

    public static areEqual(obj1: DataMapperMapping, obj2: DataMapperMapping): boolean {
        if (!obj1 || !obj2 || !obj1.sourceTable || obj1.targetTable || !obj2.sourceTable || obj2.targetTable) {
            return false;
        }

        if (obj1.sourceTable.tableName === obj2.sourceTable.tableName &&
            obj1.targetTable.tableName === obj2.targetTable.tableName &&
            MapperConnectionReference.areEqual(obj1.sourceConnectionReference, obj2.sourceConnectionReference) &&
            MapperConnectionReference.areEqual(obj1.targetConnectionReference, obj2.targetConnectionReference)) {
                return true;
        }

        return false;
    }

    public resetSource(): void {
        this.sourceTable = undefined;
        this.sourceConnectionReference = undefined;
        this._reset();
    }

    public resetTarget(): void {
        this.targetTable = undefined;
        this.targetConnectionReference = undefined;
        this.showSchemaUpdateWarning = false;
        this.showSchemaUpdateOptions = false;
        this._reset();
    }

    public setKeysForTargetEntity(keys: string[]) {
        if (!!this.targetTable && !!this.targetTable.dslConnectorProperties) {
            this.targetTable.dslConnectorProperties.set(
                DslConnectorConstants.keys,
                keys);
            if (keys.length === 0) {
                this.targetTable.dslConnectorProperties.delete(DslConnectorConstants.keys);
            }
            const value = keys.length > 0;
            this.targetTable.dslConnectorProperties.set(DslConnectorConstants.deletable, value);
            this.targetTable.dslConnectorProperties.set(DslConnectorConstants.updateable, value);
            this.targetTable.dslConnectorProperties.set(DslConnectorConstants.upsertable, value);
            this.targetTable.dslConnectorProperties.set(DslConnectorConstants.defaultToUpsert, value);
        }
    }

    public isSourceTargetDrifted(): boolean {
        return this.isValidMapping() ? (this.sourceTable.isSchemaDrifted === true || this.targetTable.isSchemaDrifted === true) : false;
    }

    public addAttributeMapping(attributeMapping: MapperAttributeMapping): MapperAttributeMapping {
        this.attributeMappings.push(attributeMapping);
        return attributeMapping;
    }

    public removeAttributeMapping(name: string): void {
        this.attributeMappings = this.attributeMappings.filter((item) => item.name !== name);
    }

    public removeAttributeMappingByIndex(index: number): void {
        this.attributeMappings.splice(index, 1);
    }

    public addSourceDenormalizeEntity(entity: IMapperTableDenormalizeEntity): void {
        this.sourceDenormalizeInfo.entities.push(entity);
    }

    public removeSourceDenormalizeEntity(entityName: string): void {
        this.sourceDenormalizeInfo.entities.filter((item) => item.toEntity !== entityName);
    }

    public getTargetColumnsFromMappings(): string[] {
        if (!this.attributeMappings || this.attributeMappings.length === 0) {
            return [];
        }

        return this.attributeMappings.map((am) => am.name);
    }

    public getNumberOfFullyMappedTargetColumns(): number {
        if (!this.attributeMappings || this.attributeMappings.length === 0) {
            return 0;
        }
        let numFullyMapped: number = 0;

        this.attributeMappings.forEach((attributeMapping) => {
            switch (attributeMapping.mappingType) {
                case MappingType.DIRECT:
                case MappingType.DERIVED:
                case MappingType.AGGREGATE: {
                    if (this._isValidGenericAttributeMapping(attributeMapping)) {
                        numFullyMapped += 1;
                    }
                    break;
                }
                case MappingType.ADVANCED: {
                    if (this._isValidAdvancedAttributeMapping(attributeMapping)) {
                        numFullyMapped += 1;
                    }
                    break;
                }
                case MappingType.SURROGATEKEY: {
                    if (this._isValidSurrogateKeyAttributeMapping(attributeMapping)) {
                        numFullyMapped += 1;
                    }
                    break;
                }
                case MappingType.LOOKUP: {
                    if (this._isValidLookUpAttributeMapping(attributeMapping)) {
                        numFullyMapped += 1;
                    }
                    break;
                }
                case MappingType.UNPIVOT: {
                    if (this._isValidUnpivotAttributeMapping(attributeMapping)) {
                        numFullyMapped += 1;
                    }
                    break;
                }
                default:
            }
        });

        return numFullyMapped;
    }

    public toSynapseDataMapperMappingDirectives(): MappingDirective {
        const mappingDirective = new MappingDirective(this.name);
        mappingDirective.targetEntityReference = new AttributeReference(this.targetTable?.tableName, '');
        mappingDirective.sourceEntityReferences = [];

        mappingDirective.sourceEntityReferences.push(new AttributeReference(this.sourceTable?.tableName, ''));

        mappingDirective.sourceDenormalizeInfo = this._toSynapseDenormalizeInfo(this.sourceDenormalizeInfo);
        mappingDirective.attributeMappingInfo = this._toSynapseAttributeMappings(this.attributeMappings);

        return mappingDirective;
    }

    public isValidMapping(): boolean {
        return !!this.sourceTable &&
                !!this.sourceTable.tableName &&
                this.sourceConnectionReference?.isValidReference() === true &&
                !!this.targetTable &&
                !!this.targetTable.tableName &&
                this.targetConnectionReference?.isValidReference() === true;
    }

    private _reset(): void {
        this.attributeMappings = [];
        this.isAttributeAutoMappingDone = false;
        this.sourceDenormalizeInfo = undefined;
        this.isSourceTargetSchemaLoading = false;
        this.hasSchemaLoadingError = undefined;
        this.showSchemaLoadingError = undefined;
        this.schemaLoadingErrorMessage = undefined;
        this.numUnMappedColumns = 0;
    }

    private _isValidGenericAttributeMapping(attributeMapping: MapperAttributeMapping): boolean {
        return !!attributeMapping
            && !!attributeMapping.name
            && !!attributeMapping.attributeReference
            && !!attributeMapping.attributeReference.isValidReference();
    }

    private _isValidAdvancedAttributeMapping(attributeMapping: MapperAttributeMapping): boolean {
        return !!attributeMapping
            && !!attributeMapping.name
            && !!this._isValidAttributeReferenences(attributeMapping.attributeReferences);
    }

    private _isValidAttributeReferenences(attributeReferences: MapperAttributeReference[]): boolean {
        if (!!attributeReferences && attributeReferences.length > 0) {
            return attributeReferences.find((attributeReference) => !attributeReference.isValidReference()) === undefined;
        }

        return false;
    }

    private _isValidSurrogateKeyAttributeMapping(attributeMapping: MapperAttributeMapping): boolean {
        return !!attributeMapping
            && !!attributeMapping.name
            && !!attributeMapping.alternateKeys
            && attributeMapping.alternateKeys.length > 0;
    }

    private _isValidLookUpAttributeMapping(attributeMapping: MapperAttributeMapping): boolean {
        return !!attributeMapping
            && !!attributeMapping.name
            && !!attributeMapping.lookupFrom
            && !!attributeMapping.lookupFrom.entity
            && !!attributeMapping.lookupFrom.attribute
            && this._isValidLookUpJoinPairs(attributeMapping.lookupFrom.joinPairs);
    }

    private _isValidUnpivotAttributeMapping(attributeMapping: MapperAttributeMapping): boolean {
        return !!attributeMapping
            && !!attributeMapping.name
            && !!attributeMapping.unpivotInfo
            && !!attributeMapping.unpivotInfo.targetUnpivotKeyAttribute
            && this._isValidUnpivotAttributes(attributeMapping.unpivotInfo.sourceUnpivotAttributes);
    }

    private _isValidUnpivotAttributes(unpivotAttributes: IMapperUnpivotAttribute[]): boolean {
        if (!!unpivotAttributes && unpivotAttributes.length > 0) {
            for (const unpivotAttribute of unpivotAttributes) {
                if (!this._isValidUnpivotAttribute(unpivotAttribute)) {
                    return false;
                }
            }

            return true;
        }

        return false;
    }

    private _isValidUnpivotAttribute(unpivotAttribute: IMapperUnpivotAttribute): boolean {
        return !!unpivotAttribute
            && !!unpivotAttribute.unpivotKeyValue
            && !!unpivotAttribute.attributeReference
            && unpivotAttribute.attributeReference.isValidReference();
    }

    private _isValidLookUpJoinPairs(joinPairs: IMapperJoinPair[]): boolean {
        if (!!joinPairs && joinPairs.length > 0) {
            for (const joinPair of joinPairs) {
                if (!this._isValidLookUpJoinPair(joinPair)) {
                    return false;
                }
            }

            return true;
        }

        return false;
    }

    private _isValidLookUpJoinPair(joinPair: IMapperJoinPair): boolean {
        return !!joinPair
            && !!joinPair.fromAttribute
            && !!joinPair.fromAttribute.isValidReference()
            && !!joinPair.toAttribute
            && !!joinPair.operator;
    }

    private _toSynapseAttributeMappings(adfMapperAttributeMappings: MapperAttributeMapping[]): AttributeMapping[] {
        const synapseAttributeMapping: AttributeMapping[] = [];
        if (adfMapperAttributeMappings) {
            adfMapperAttributeMappings.forEach((adfMapperAttributeMapping) => {
                synapseAttributeMapping.push(this._toSynapseAttributeMapping(adfMapperAttributeMapping));
            });
        }

        return synapseAttributeMapping;
    }

    private _toSynapseAttributeMapping(adfMapperAttributeMapping: MapperAttributeMapping): AttributeMapping {
        if (!!adfMapperAttributeMapping) {
            const attrMappingModel: AttributeMapping = new AttributeMapping(adfMapperAttributeMapping.name);
            attrMappingModel.mappingType = adfMapperAttributeMapping.mappingType;
            attrMappingModel.attributeReference = this._toSynapseAttributeReference(adfMapperAttributeMapping.attributeReference);
            attrMappingModel.attributeReferences = this._toSynapseAttributeReferences(adfMapperAttributeMapping.attributeReferences);
            attrMappingModel.expression =  adfMapperAttributeMapping.expression;
            attrMappingModel.alternateKeys = adfMapperAttributeMapping.alternateKeys;
            attrMappingModel.lookupFrom = this._toSynapsLookupInfo(adfMapperAttributeMapping.lookupFrom);
            attrMappingModel.unpivotInfo = this._toSynapsUnpivotInfo(adfMapperAttributeMapping.unpivotInfo);
            attrMappingModel.functionName = adfMapperAttributeMapping.functionName;
            attrMappingModel.isSystemGenerated = adfMapperAttributeMapping.isSystemGenerated;
            attrMappingModel.isRecommended = adfMapperAttributeMapping.isRecommended;

            return attrMappingModel;
        }

        return undefined;
    }

    private _toSynapsLookupInfo(adfDataMapperLookupInfo: IMapperLookupInfo): ILookupInfo {
        if (!!adfDataMapperLookupInfo) {
            return {
                entity: adfDataMapperLookupInfo.entity,
                attribute: adfDataMapperLookupInfo.attribute,
                joinPairs: this._toSynapseJoinPairs(adfDataMapperLookupInfo.joinPairs)
            };
        }

        return undefined;
    }

    private _toSynapsUnpivotInfo(adfDataMapperUnpivotInfo: IMapperUnpivotInfo): IUnpivotInfo {
        if (!!adfDataMapperUnpivotInfo) {
            return {
                targetUnpivotKeyAttribute: adfDataMapperUnpivotInfo.targetUnpivotKeyAttribute,
                sourceUnpivotAttributes: this._toSynapsUnpivotAttributes(adfDataMapperUnpivotInfo.sourceUnpivotAttributes)
            };
        }

        return undefined;
    }

    private _toSynapsUnpivotAttributes(adfDataMapperUnpivotAttributes: IMapperUnpivotAttribute[]): IUnpivotAttribute[] {
        const synapseUnpivotAttributes: IUnpivotAttribute[] = [];
        if (!!adfDataMapperUnpivotAttributes) {
            adfDataMapperUnpivotAttributes.forEach((adfDataMapperUnpivotAttribute) => synapseUnpivotAttributes.push(this._toSynapsUnpivotAttribute(adfDataMapperUnpivotAttribute)));
        }

        return synapseUnpivotAttributes;
    }

    private _toSynapsUnpivotAttribute(adfDataMapperUnpivotAttribute: IMapperUnpivotAttribute): IUnpivotAttribute {
        if (!!adfDataMapperUnpivotAttribute) {
            return {
                unpivotKeyValue: adfDataMapperUnpivotAttribute.unpivotKeyValue,
                attributeReference: this._toSynapseAttributeReference(adfDataMapperUnpivotAttribute.attributeReference)
            };
        }

        return undefined;
    }

    private _toSynapseDenormalizeInfo(adfDataMapperDenormalizeInfo: IMapperTableDenormalizeInfo): IDenormalizeInfo {
        if (!!adfDataMapperDenormalizeInfo) {
            return {
                entities: this._toSynapseDenormalizeEntities(adfDataMapperDenormalizeInfo.entities)
            };
        }

        return undefined;
    }

    private _toSynapseDenormalizeEntities(adfDataMapperDenormalizeEntities: IMapperTableDenormalizeEntity[]): IDenormalizeEntity[] {
        const synapseDenormalizeEntities: IDenormalizeEntity[] = [];
        if (!!adfDataMapperDenormalizeEntities) {
            adfDataMapperDenormalizeEntities.forEach((adfDataMapperDenormalizeEntity) => synapseDenormalizeEntities.push(this._toSynapseDenormalizeEntitiy(adfDataMapperDenormalizeEntity)));
        }

        return synapseDenormalizeEntities;
    }

    private _toSynapseDenormalizeEntitiy(adfDataMapperDenormalizeEntity: IMapperTableDenormalizeEntity): IDenormalizeEntity {
        if (!!adfDataMapperDenormalizeEntity) {
            return {
                toEntity: adfDataMapperDenormalizeEntity.toEntity,
                joinPairs: this._toSynapseJoinPairs(adfDataMapperDenormalizeEntity.joinPairs)
              };
        }

        return undefined;
    }

    private _toSynapseJoinPairs(adfDataMapperJoinPairs: IMapperJoinPair[]): IMappingJoinPair[] {
        const synapseJoinPairs: IMappingJoinPair[] = [];
        if (!!adfDataMapperJoinPairs) {
            adfDataMapperJoinPairs.forEach((adfDataMapperJoinPair) => synapseJoinPairs.push(this._toSynapseJoinPair(adfDataMapperJoinPair)));
        }

        return synapseJoinPairs;
    }

    private _toSynapseJoinPair(adfDataMapperJoinPair: IMapperJoinPair): IMappingJoinPair {
        if (!!adfDataMapperJoinPair) {
            return {
                toAttribute: adfDataMapperJoinPair.toAttribute,
                operator: adfDataMapperJoinPair.operator,
                expression: adfDataMapperJoinPair.expression,
                fromAttribute: this._toSynapseAttributeReference(adfDataMapperJoinPair.fromAttribute)
            };
        }

        return undefined;
    }

    private _toSynapseAttributeReferences(adfDataMapperAttributeReferences: MapperAttributeReference[]): AttributeReference[] {
        const synapseAttributeReferences: AttributeReference[] = [];
        if (!!adfDataMapperAttributeReferences) {
            adfDataMapperAttributeReferences.forEach((adfDataMapperAttributeReference) => synapseAttributeReferences.push(this._toSynapseAttributeReference(adfDataMapperAttributeReference)));
        }

        return synapseAttributeReferences;
    }

    private _toSynapseAttributeReference(adfDataMapperAttributeReference: MapperAttributeReference): AttributeReference {
        if (!!adfDataMapperAttributeReference) {
            return {
                name: adfDataMapperAttributeReference.columnName,
                entity: adfDataMapperAttributeReference.table?.tableName
            };
        }

        return undefined;
    }
}
